/**
 * AdsReport — every paid-social ad for a period: its picture, where it ran, and what followed.
 * Used by Analytics (the Ads page) and Engagement (the Ads view); both hand it the same
 * getAnalytics response, so the two can never disagree about an ad.
 *
 * Meta sends each ad as its own campaign id, so a campaign row is an ad; its name comes from
 * Campaign names and "View ad" opens it in Meta's public Ad Library.
 *
 * ⚠️ CLICKS and VISITS are different counts and sit in separate columns: a click is a hit on
 * the ad's go.clozesure.com link; a visit is a browser that then ran our code on the site.
 * They are never added, and a rate is always visits-based with its denominator on hover.
 *
 * Pictures are added by an admin, once per ad — pasted from the clipboard or chosen as a
 * file. The Ad Library's own image links expire within days and Meta refuses requests that
 * do not come from a browser, so a picture can be neither linked to nor fetched by the server
 * (functions/analytics/adImages.js).
 */
import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaImage } from 'react-icons/fa';
import { Panel } from '../Panel';
import MetricTile from '../MetricTile';
import Sparkline from '../Sparkline';
import InfoNotice from '../InfoNotice';
import { EmptyState } from '../EmptyState';
import { Button } from '../Button';
import { Modal } from '../Modal';
import { TextArea } from '../Form';
import { useToast } from '../Toast';
import { fmt, pct, dayLabel, SMALL_SAMPLE } from '../../../utils/analyticsFormat';
import { AD_LIBRARY_SEARCH_URL, adLibraryUrl, adsManagerUrl } from '../../../utils/metaAds';
import { isMetaImageUrl } from '../../../utils/adLibraryPaste';
import { fileToThumbnail, clipboardImage } from '../../../utils/imageThumb';
import { adRows, orderAds, adsNeedingDetails, AD_ORDERS } from '../../../utils/adRows';
import { useAdImages } from '../../../hooks/useAdImages';
import { useAdStarts } from '../../../hooks/useAdStarts';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { AnalyticsRepository } from '../../../models/AnalyticsRepository';
import { functions } from '../../../firebase';
import { SegmentedControl } from '../Toolbar';
import AdLibraryUpdate from './AdLibraryUpdate';
import './AdsReport.scss';

// The first two are counted from each ad's OWN launch, so they compare like with like; the
// rest are for the period chosen on the page.
const COLUMNS = [
  ['First day', 'Clicks by people in the 24 hours from this ad’s first click. The same span for every ad, however long it has run.'],
  ['First week', 'Clicks by people in the 7 days from this ad’s first click. The same span for every ad, however long it has run.'],
  ['Clicks', 'Hits on the ad’s link by people, in the period chosen'],
  ['Site visits', 'Browsers that then opened the site'],
  ['Explored', 'Share of visits that read on: several pages or sections, half a minute, or an action'],
  ['Reached /quote', 'Visits that got to the quote page'],
  ['Quotes', 'Visits that submitted a quote'],
];

/** "1 in 409 visits" — a rate this small reads as 0.0% as a percentage. */
const oneIn = (n, visits) => (!visits ? 'no visits in this period'
  : !n ? `none of ${fmt(visits)} visits`
    : `1 in ${fmt(Math.round(visits / n))} visits`);

/** A count that reads as "nothing" when zero. */
const Count = ({ n, strong }) => (
  <span className={!n ? 'ads-report__muted' : strong ? 'ads-report__strong' : ''}>{n ? fmt(n) : '–'}</span>
);
Count.propTypes = { n: PropTypes.number, strong: PropTypes.bool };

/** A rate with its denominator on hover; muted when the base is too small to trust. */
const Rate = ({ n, d }) => {
  if (!d) return <span className="ads-report__muted">—</span>;
  const small = d < SMALL_SAMPLE;
  return (
    <span className={small ? 'ads-report__muted' : ''} title={`${fmt(n)} of ${fmt(d)}${small ? ' — too few to trust' : ''}`}>
      {pct(n, d)}
    </span>
  );
};
Rate.propTypes = { n: PropTypes.number, d: PropTypes.number };

/**
 * A from-launch figure. Until its window has closed the number is still growing, so it is
 * shown as "so far" — it must not be read as a finished week next to finished ones.
 */
function Launch({ start, field, hours }) {
  if (!start || start.capped || start.launchMs == null) return <span className="ads-report__muted" title="No launch figures for this ad">–</span>;
  const closed = start.hoursSoFar >= hours;
  const days = Math.floor(start.hoursSoFar / 24);
  const title = closed
    ? `${fmt(start[field])} clicks in the ${hours === 24 ? '24 hours' : '7 days'} from its first click on ${dayLabel(start.launchDay)}. First 48 hours: ${fmt(start.day2)}.`
    : `Launched ${dayLabel(start.launchDay)}: ${hours === 24 ? `${start.hoursSoFar} of 24 hours` : `${days} of 7 days`} so far, so this will still rise.`;
  return (
    <span title={title} className={closed ? 'ads-report__strong' : 'ads-report__sofar'}>
      {fmt(start[field])}
      {!closed && <small> so far</small>}
    </span>
  );
}
Launch.propTypes = { start: PropTypes.object, field: PropTypes.string.isRequired, hours: PropTypes.number.isRequired };

function Stat({ label, children }) {
  return (
    <div className="ads-report__stat">
      <span className="ads-report__stat-label">{label}</span>
      {children}
    </div>
  );
}
Stat.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

export default function AdsReport({ data, color, isAdmin = false, note, onChanged }) {
  const toast = useToast();
  const { images, texts, setImage, setText } = useAdImages();
  const rows = useMemo(() => adRows(data && data.ads, data && data.campaigns), [data]);
  const metaKeys = useMemo(() => rows.filter((a) => a.metaAd).map((a) => a.campaign), [rows]);
  const { starts } = useAdStarts(metaKeys);
  const [order, setOrder] = usePersistedState('adsReport.order', 'week');
  const ordered = useMemo(() => orderAds(rows, starts, order), [rows, starts, order]);
  const needing = useMemo(() => adsNeedingDetails(rows, images, texts), [rows, images, texts]);
  const repo = useMemo(() => new AnalyticsRepository(functions), []);
  const [updating, setUpdating] = useState(false);
  const [busy, setBusy] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [textDraft, setTextDraft] = useState(null); // the ad text being edited in the details box
  const picker = useRef(null);
  const pickFor = useRef(null);

  // An older backend returns no ads section; say so rather than drawing an empty table.
  if (!data || !data.ads) {
    return <InfoNotice variant="info">The Ads report needs the latest Analytics update, which has not been deployed yet.</InfoNotice>;
  }
  const T = data.ads.totals;

  // One save path for both fields, so a failure is reported the same way.
  const saveField = async (key, field, value, done) => {
    setBusy(key);
    try {
      await (field === 'image' ? setImage(key, value) : setText(key, value));
      toast.success(done);
      return true;
    } catch (error) {
      toast.error(error.message || 'Could not save', {
        debugInfo: {
          operation: 'setCampaignAdImageCallable',
          params: { key, field, chars: value ? value.length : 0 },
          error: error.stack || error.toString(),
          context: 'AdsReport',
        },
      });
      return false;
    } finally {
      setBusy(null);
    }
  };
  const save = (key, image, done) => saveField(key, 'image', image, done);
  const openDetails = (a) => { setZoom(a); setTextDraft(null); };
  // Any picture source → shrunk in the browser → stored against the ad.
  const store = async (key, getFile) => {
    let image;
    try {
      image = await fileToThumbnail(await getFile());
    } catch (error) {
      toast.warning(error.message || 'That picture could not be used.');
      return;
    }
    await save(key, image, 'Picture saved');
  };
  const choose = (key) => { pickFor.current = key; if (picker.current) picker.current.click(); };
  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    const key = pickFor.current;
    e.target.value = ''; // choosing the same file again must still fire
    if (file && key != null) store(key, () => file);
  };

  // One ad from the Ad Library update: write only what the ad is missing. Each part is
  // tried on its own, so a picture that cannot be fetched does not lose the name.
  const saveFromLibrary = async (a, { ad, name }) => {
    const notes = [];
    let ok = true;
    const attempt = async (what, fn) => {
      try { await fn(); } catch (error) { ok = false; notes.push(`The ${what} was not saved: ${error.message || 'unknown error'}.`); }
    };
    if (a.missing.name || a.missing.link) {
      await attempt(a.missing.name ? 'name' : 'link', () => repo.setCampaignLabel({
        key: a.campaign,
        ...(a.missing.name ? { label: name } : {}),
        ...(a.missing.link ? { adLibraryId: ad.libraryId } : {}),
      }));
    }
    if (a.missing.text && ad.text) await attempt('wording', () => setText(a.campaign, ad.text.slice(0, 2000)));
    if (a.missing.picture) {
      if (!ad.imageUrl) notes.push('No picture came with the paste: add it with “Paste picture” on the ad.');
      else {
        await attempt('picture', async () => {
          // Meta's image host allows a browser to read its pictures (it sends
          // Access-Control-Allow-Origin: *), so no server fetch is needed.
          // ⚠️ Checked again HERE, at the fetch: the paste reader that filters addresses is in
          // another file. No redirect is followed, and neither a cookie nor this page's
          // address is sent to Meta's host.
          if (!isMetaImageUrl(ad.imageUrl)) throw new Error('the picture address is not on Meta’s image host');
          const res = await fetch(ad.imageUrl, { redirect: 'error', credentials: 'omit', referrerPolicy: 'no-referrer' });
          if (!res.ok) throw new Error(`the picture address answered ${res.status}`);
          await setImage(a.campaign, await fileToThumbnail(await res.blob()));
        });
      }
    }
    return { ok, notes };
  };

  return (
    <div className="ads-report">
      <div className="ads-report__tiles">
        <MetricTile label="Ad clicks by people" value={T.clicks} color={color}
          hint={`${fmt(rows.length)} ad${rows.length === 1 ? '' : 's'} with clicks or visits in this period`} />
        <MetricTile label="Site visits from ads" value={T.sessions} color="#7c3aed"
          hint="browsers that opened the site — counted apart from clicks" />
        <MetricTile label="Reached /quote" value={T.reachedQuote} color="#0d9488" hint={oneIn(T.reachedQuote, T.sessions)} />
        <MetricTile label="Quotes submitted" value={T.submitted} color="#15803d" hint={oneIn(T.submitted, T.sessions)} />
      </div>

      {isAdmin && needing.length > 0 && (
        <InfoNotice variant="info">
          <strong>{needing.length === 1 ? 'One ad has' : `${fmt(needing.length)} ads have`} details missing</strong>{' '}
          (a name, its Ad Library link, wording or picture).{' '}
          <Button size="small" variant="primary" onClick={() => setUpdating(true)}>Update from Ad Library</Button>
        </InfoNotice>
      )}

      <Panel
        title="Every ad in this period"
        actions={(
          <span className="ads-report__order">
            <span>Order by</span>
            <SegmentedControl options={AD_ORDERS} value={order} onChange={setOrder} />
          </span>
        )}
        subtitle={(
          <>
            <strong>First day</strong> and <strong>First week</strong> are counted from each ad’s own launch, so an ad
            that has run a week can be compared fairly with one that has run a month. The other figures are for
            the period chosen. Clicks are hits on the ad’s link; visits are browsers that then opened the site:
            different counts, never added together.
            {note ? ` ${note}` : ''}
          </>
        )}
      >
        {rows.length === 0 ? (
          <EmptyState icon={<FaImage />} title="No ad traffic in this period"
            description="No paid-social ad brought a click or a visit. Try a longer period." />
        ) : (
          <div className="ads-report__table" role="table" aria-label="Ads in this period">
            <div className="ads-report__row ads-report__row--head" role="row">
              <span role="columnheader" className="ads-report__head-ad">Ad</span>
              {COLUMNS.map(([label, help]) => <span key={label} role="columnheader" title={help}>{label}</span>)}
              <span role="columnheader" title="Clicks on each day of the period">Daily clicks</span>
            </div>
            {ordered.map((a) => {
              const image = images[a.campaign];
              const view = adLibraryUrl(a.adLibraryId);
              const saving = busy === a.campaign;
              return (
                <div key={a.campaign || '(none)'} className="ads-report__row" role="row" aria-label={a.label}>
                  <div className="ads-report__thumb" role="cell">
                    {image ? (
                      <button type="button" className="ads-report__pic" onClick={() => openDetails(a)} title="Show the picture and the ad's text">
                        <img src={image} alt={`The ad: ${a.label}`} />
                      </button>
                    ) : isAdmin ? (
                      <button type="button" className="ads-report__pic ads-report__pic--empty" disabled={saving}
                        onClick={() => choose(a.campaign)} title="Choose a picture of this ad from your computer">
                        <FaImage aria-hidden="true" />
                        <span>{saving ? 'Saving…' : 'Add picture'}</span>
                      </button>
                    ) : (
                      <span className="ads-report__pic ads-report__pic--empty" title="No picture has been added for this ad">
                        <FaImage aria-hidden="true" />
                      </span>
                    )}
                  </div>
                  <div className="ads-report__name" role="cell">
                    <strong className={a.named ? '' : 'ads-report__muted'} title={a.campaign ? `Campaign id ${a.campaign}` : undefined}>{a.label}</strong>
                    <span className="ads-report__meta" title={`Where its ${a.placementsOf} came from, and the days in this period it was clicked`}>
                      {[
                        ...a.placements.map((p) => `${p.label} ${Math.round(p.share * 100)}%`),
                        a.firstDay && (a.firstDay === a.lastDay ? `clicked ${dayLabel(a.firstDay)}` : `clicked ${dayLabel(a.firstDay)} – ${dayLabel(a.lastDay)}`),
                      ].filter(Boolean).join(' · ')}
                    </span>
                    {texts[a.campaign] && <span className="ads-report__text" title={texts[a.campaign]}>{texts[a.campaign]}</span>}
                    <span className="ads-report__links">
                      {a.metaAd && (view
                        ? <a href={view} target="_blank" rel="noopener noreferrer">View ad ↗</a>
                        : <a href={AD_LIBRARY_SEARCH_URL} target="_blank" rel="noopener noreferrer" title="Opens Meta's Ad Library with every ClozeSure ad. Add this ad's link in Campaign names.">Find this ad ↗</a>)}
                      {adsManagerUrl(a.campaign) && (
                        <a href={adsManagerUrl(a.campaign)} target="_blank" rel="noopener noreferrer"
                          title="Opens this ad's campaign in Meta Ads Manager. Only for someone with access to the ad account.">
                          Ads Manager ↗
                        </a>
                      )}
                      {(isAdmin || texts[a.campaign] || image) && (
                        <Button variant="link" size="small" onClick={() => openDetails(a)}>Details</Button>
                      )}
                      {isAdmin && !image && (
                        <Button variant="link" size="small" disabled={saving}
                          title="Copy the ad's image (right-click it in the Ad Library, Copy image), then press this"
                          onClick={() => store(a.campaign, clipboardImage)}>
                          {saving ? 'Saving…' : 'Paste picture'}
                        </Button>
                      )}
                    </span>
                  </div>
                  <div className="ads-report__stats">
                    <Stat label="First day"><Launch start={starts[a.campaign]} field="day1" hours={24} /></Stat>
                    <Stat label="First week"><Launch start={starts[a.campaign]} field="week" hours={168} /></Stat>
                    <Stat label="Clicks"><Count n={a.clicks} /></Stat>
                    <Stat label="Site visits"><Count n={a.sessions} /></Stat>
                    <Stat label="Explored"><Rate n={a.explored} d={a.sessions} /></Stat>
                    <Stat label="Reached /quote"><Count n={a.reachedQuote} /></Stat>
                    <Stat label="Quotes"><Count n={a.submitted} strong /></Stat>
                  </div>
                  <div className="ads-report__daily" role="cell">
                    <Sparkline data={a.daily} width={104} height={26} color={color} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <input ref={picker} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onFile}
          aria-label="Choose a picture of the ad" />
      </Panel>

      {zoom && (
        <Modal isOpen onClose={() => setZoom(null)} title={zoom.label} size="small">
          <div className="ads-report__zoom" role="group" aria-label={`Details of ${zoom.label}`}>
            {images[zoom.campaign] && <img src={images[zoom.campaign]} alt={`The ad: ${zoom.label}`} />}
            {isAdmin && (
              <span className="ads-report__links">
                <Button variant="link" size="small" disabled={busy === zoom.campaign}
                  title="Copy the ad's image (right-click it in the Ad Library, Copy image), then press this"
                  onClick={() => store(zoom.campaign, clipboardImage)}>
                  {images[zoom.campaign] ? 'Paste new picture' : 'Paste picture'}
                </Button>
                <Button variant="link" size="small" disabled={busy === zoom.campaign} onClick={() => choose(zoom.campaign)}>Choose file</Button>
                {images[zoom.campaign] && (
                  <Button variant="link" size="small" disabled={busy === zoom.campaign}
                    onClick={() => save(zoom.campaign, '', 'Picture removed')}>
                    Remove picture
                  </Button>
                )}
              </span>
            )}
            {textDraft === null ? (
              <>
                {texts[zoom.campaign]
                  ? <p>{texts[zoom.campaign]}</p>
                  : <p className="ads-report__muted">No ad text has been added.</p>}
                {isAdmin && (
                  <Button variant="link" size="small" onClick={() => setTextDraft(texts[zoom.campaign] || '')}>
                    {texts[zoom.campaign] ? 'Edit the ad text' : 'Add the ad text'}
                  </Button>
                )}
              </>
            ) : (
              <>
                <TextArea value={textDraft} onChange={(e) => setTextDraft(e.target.value)} rows={7}
                  placeholder="Paste the ad's wording from Meta's Ad Library" aria-label={`Ad text for ${zoom.label}`} />
                <span className="ads-report__links">
                  <Button size="small" variant="primary" disabled={busy === zoom.campaign}
                    onClick={async () => {
                      const ok = await saveField(zoom.campaign, 'text', textDraft.trim(), textDraft.trim() ? 'Ad text saved' : 'Ad text removed');
                      if (ok) setTextDraft(null);
                    }}>
                    {busy === zoom.campaign ? 'Saving…' : 'Save text'}
                  </Button>
                  <Button size="small" variant="link" onClick={() => setTextDraft(null)}>Cancel</Button>
                </span>
              </>
            )}
            <span className="ads-report__links">
              {adLibraryUrl(zoom.adLibraryId) && (
                <a href={adLibraryUrl(zoom.adLibraryId)} target="_blank" rel="noopener noreferrer">View the ad in Meta’s Ad Library ↗</a>
              )}
              {adsManagerUrl(zoom.campaign) && (
                <a href={adsManagerUrl(zoom.campaign)} target="_blank" rel="noopener noreferrer">Ads Manager ↗</a>
              )}
            </span>
          </div>
        </Modal>
      )}

      {updating && (
        <AdLibraryUpdate
          ads={needing}
          starts={starts}
          usedLibraryIds={rows.map((a) => a.adLibraryId).filter(Boolean)}
          onSaveAd={saveFromLibrary}
          onClose={() => setUpdating(false)}
          onDone={() => { toast.success('Ad details saved'); if (onChanged) onChanged(); }}
        />
      )}
    </div>
  );
}

AdsReport.propTypes = {
  /** A getAnalytics response carrying `ads` and `campaigns`. */
  data: PropTypes.object,
  /** The tool's accent (createToolTheme(tool.color).color, or tool.color). */
  color: PropTypes.string,
  /** Admins can add, replace and remove an ad's picture. */
  isAdmin: PropTypes.bool,
  /** One sentence added to the panel's subtitle, e.g. how this view's window is counted. */
  note: PropTypes.string,
  /** Called after ad details were saved from the Ad Library, so the host can reload its names. */
  onChanged: PropTypes.func,
};
